import { RolodexEntryType } from 'types'
import { DataProcessingConfigSchema } from 'types/timeSeries/dataProcessing'
import { z } from 'zod'

const TagsSchema = z.record(z.string(), z.string().nullish())

const OptionalTimestamps = z.object({
  updated_at: z.coerce.date().nullish(),
  created_at: z.coerce.date().nullish(),
  created_by: z.string().nullish(),
  last_updated_by: z.string().nullish(),
})

export const ChartTimeUnitSchema = z.enum([
  'seconds',
  'minutes',
  'hours',
  'days',
  'weeks',
  'months',
])
export type ChartTimeUnit = z.infer<typeof ChartTimeUnitSchema>

export const ChartTimeUnit = {
  SECONDS: 'seconds',
  MINUTES: 'minutes',
  HOURS: 'hours',
  DAYS: 'days',
  WEEKS: 'weeks',
  MONTHS: 'months',
} as const satisfies Record<string, ChartTimeUnit>

export const YAxisModeSchema = z.enum(['single', 'multiple'])
export type YAxisMode = z.infer<typeof YAxisModeSchema>

export const SourceTypeSchema = z.enum(['sensor', 'derivedValue', 'sentinel'])
export type SourceType = z.infer<typeof SourceTypeSchema>

export const YAxisRangeSchema = z
  .object({
    min: z.number(),
    max: z.number(),
  })
  .refine((v) => v.max >= v.min, {
    message: 'yAxisRange.max must be ≥ min',
    path: ['max'],
  })

export const SourceConfigSchema = z
  .object({
    type: SourceTypeSchema,
    source_id: z.string(),
    color: z.string(),
    hidden: z.boolean().nullish(),
    y_axis_range: YAxisRangeSchema.nullish(),
    // Sentinel-only fields (`type: 'sentinel'`, where source_id is the
    // sentinel's Rolodex entry id). Nullish so charts saved before sentinels
    // were persisted — and sensor/derivedValue configs, which never carry
    // them — parse unchanged. The target tag is stored by datapoint entry id;
    // the target PEN is persisted separately as its own sensor config, so
    // loading a sentinel must not re-add it.
    sentinel_target: z.string().nullish(),
    sentinel_name: z.string().nullish(),
    sentinel_method: z.string().nullish(),
  })
  .transform((data) => {
    return {
      type: data.type,
      sourceId: data.source_id,
      color: data.color,
      hidden: data.hidden,
      yAxisRange: data.y_axis_range,
      // Conditional spreads keep these OPTIONAL in the inferred type, so the
      // sensor/derivedValue builders don't have to write `undefined` keys.
      ...(data.sentinel_target != null && {
        sentinelTarget: data.sentinel_target,
      }),
      ...(data.sentinel_name != null && { sentinelName: data.sentinel_name }),
      ...(data.sentinel_method != null && {
        sentinelMethod: data.sentinel_method,
      }),
    }
  })

/** Which in-chart tool is open. `none` means plain pan/zoom. */
export const ChartInteractionModeSchema = z.enum([
  'none',
  'ruler',
  'point',
  'label',
])
export type ChartInteractionMode = z.infer<typeof ChartInteractionModeSchema>

/**
 * A ruler's persisted form.
 *
 * Deliberately stores only the x-axis *domain* values, never the
 * `startPixelOnOverlay`/`endPixelOnOverlay` the live `Ruler` also carries: those
 * are viewport-derived, so replaying them at a different window size or zoom
 * would restore the ruler to a visually wrong span. `RulerOverlay` recomputes
 * the pixels from these values via `valToPos` on every render, so the domain
 * values are the only durable truth.
 */
export const RulerConfigSchema = z
  .object({
    id: z.string(),
    start_x_value: z.number(),
    end_x_value: z.number(),
    selected_sensor_ids: z.array(z.string()),
    is_minimized: z.boolean().nullish(),
    panel_offset_y: z.number().nullish(),
  })
  .transform((data) => {
    return {
      id: data.id,
      startXValue: data.start_x_value,
      endXValue: data.end_x_value,
      selectedSensorIds: data.selected_sensor_ids,
      isMinimized: data.is_minimized,
      panelOffsetY: data.panel_offset_y,
    }
  })

/**
 * A point annotation's persisted form. `anchor_y_ratio` is plot-relative (0 = top
 * of the plot area, 1 = bottom), so it survives a resize as long as the y-range
 * is restored — which `source_configs.y_axis_range` already does.
 */
export const PointAnnotationConfigSchema = z
  .object({
    id: z.string(),
    anchor_timestamp: z.number(),
    anchor_y_ratio: z.number(),
    sensor_id: z.string().nullish(),
  })
  .transform((data) => {
    return {
      id: data.id,
      anchorTimestamp: data.anchor_timestamp,
      anchorYRatio: data.anchor_y_ratio,
      sensorId: data.sensor_id ?? null,
    }
  })

export const DateModeSchema = z.enum(['absolute', 'relative'])
export type DateMode = z.infer<typeof DateModeSchema>

const AbsoluteDateConfigSchema = z
  .object({
    mode: z.literal('absolute'),
    start_date: z.coerce.date(),
    end_date: z.coerce.date(),
  })
  .transform((data) => {
    return {
      mode: data.mode,
      startDate: data.start_date,
      endDate: data.end_date,
    }
  })

const RelativeLookbackSchema = z.object({
  value: z.number(),
  unit: ChartTimeUnitSchema,
})

const RelativeDateConfigSchema = z
  .object({
    mode: z.literal('relative'),
    start_date_lookback: RelativeLookbackSchema,
    end_date_lookback: RelativeLookbackSchema.nullish(),
    update_cadence_seconds: z.number().int().positive(),
  })
  .transform((data) => {
    return {
      mode: data.mode,
      startDateLookback: data.start_date_lookback,
      endDateLookback: data.end_date_lookback,
      updateCadenceSeconds: data.update_cadence_seconds,
    }
  })

export const DateConfigSchema = z.discriminatedUnion('mode', [
  AbsoluteDateConfigSchema,
  RelativeDateConfigSchema,
])

// ---------- chart data ----------
// Downstream: requires upstreamChartId; others optional
export const DownstreamChartSchema = z
  .object({
    name: z.string().nullish(),
    description: z.string().nullish(),
    data_processing_configs: z.array(DataProcessingConfigSchema).nullish(),
    upstream_chart_id: z.string(),
    y_axis_mode: YAxisModeSchema.nullish(),
    source_configs: z.array(SourceConfigSchema).nullish(),
    date_config: DateConfigSchema.nullish(),
  })
  .transform((data) => {
    return {
      name: data.name,
      description: data.description,
      dataProcessingConfigs: data.data_processing_configs,
      upstreamChartId: data.upstream_chart_id,
      yAxisMode: data.y_axis_mode,
      sourceConfigs: data.source_configs,
      dateConfig: data.date_config,
    }
  })

export const StandaloneChartSchema = z
  .object({
    name: z.string().nullish(),
    description: z.string().nullish(),
    data_processing_configs: z.array(DataProcessingConfigSchema).nullish(),
    y_axis_mode: YAxisModeSchema,
    source_configs: z.array(SourceConfigSchema),
    date_config: DateConfigSchema,
    // Nullish so charts saved before these fields existed still parse: a missing
    // key reads back as `undefined`, which every loader treats as "no tool state".
    ruler_configs: z.array(RulerConfigSchema).nullish(),
    point_annotation_configs: z.array(PointAnnotationConfigSchema).nullish(),
    interaction_mode: ChartInteractionModeSchema.nullish(),
  })
  .transform((data) => {
    return {
      name: data.name,
      description: data.description,
      dataProcessingConfigs: data.data_processing_configs,
      yAxisMode: data.y_axis_mode,
      sourceConfigs: data.source_configs,
      dateConfig: data.date_config,
      rulerConfigs: data.ruler_configs,
      pointAnnotationConfigs: data.point_annotation_configs,
      interactionMode: data.interaction_mode,
    }
  })

export const ChartDataSchema = z.union([
  DownstreamChartSchema,
  StandaloneChartSchema,
])

// ---------- entry wrappers ----------
export const ChartEntryJSONSchema = z
  .object({
    id: z.string(),
    type: z.literal(RolodexEntryType.CHART),
    data: ChartDataSchema,
    tags: TagsSchema,
    ...OptionalTimestamps.shape,
  })
  .transform((schema) => ({
    id: schema.id,
    type: schema.type,
    data: schema.data,
    tags: schema.tags,
    ...(schema.updated_at && { updatedAt: schema.updated_at }),
    ...(schema.created_at && { createdAt: schema.created_at }),
    ...(schema.created_by && { createdBy: schema.created_by }),
    ...(schema.last_updated_by && {
      lastUpdatedBy: schema.last_updated_by,
    }),
  }))

export const ChartEntrySchema = z.object({
  entry: ChartEntryJSONSchema,
})

// ---------- inferred types ----------
export type YAxisRange = z.infer<typeof YAxisRangeSchema>
export type RulerConfig = z.infer<typeof RulerConfigSchema>
export type PointAnnotationConfig = z.infer<typeof PointAnnotationConfigSchema>
export type SourceConfig = z.infer<typeof SourceConfigSchema>
export type DateConfig = z.infer<typeof DateConfigSchema>
export type DownstreamChart = z.infer<typeof DownstreamChartSchema>
export type StandaloneChart = z.infer<typeof StandaloneChartSchema>
export type ChartData = z.infer<typeof ChartDataSchema>
export type ChartEntryJSON = z.infer<typeof ChartEntryJSONSchema>
export type ChartEntry = z.infer<typeof ChartEntrySchema>
